<template>
  <ts-card>
    <div
      class="form-header ts-bg-gray-100 ts-border-b ts-border-gray-400 ts-p-4 ts-relative"
    >
      <h4 class="ts-text-sm ts-text-blue-600">{{ title }}</h4>
      <ts-modal-close-button />
    </div>
    <ts-card-wrapper>
      <div class="form-group">
        <label class="control-label required">{{
          $t('schedule.subjectName')
        }}</label>
        <div class="searchable-select">
          <input
            type="text"
            class="form-control"
            v-model="subjectSearch"
            :placeholder="$t('typeForSearch')"
            @focus="handleSubjectFocus"
            @blur="handleSubjectBlur"
            @input="handleSubjectInput"
          />
          <div
            v-if="showSubjectDropdown && filteredSubjects.length > 0"
            class="dropdown-list"
          >
            <div
              v-for="subject in filteredSubjects"
              :key="subject.subject_id"
              class="dropdown-item"
              @mousedown.prevent="selectSubject(subject)"
            >
              {{ subject.subject_name + ' | ' + subject.subject_code }}
            </div>
          </div>
        </div>
        <div class="validate" v-if="errors.has('subject_id')">
          {{ errors.first('subject_id') }}
        </div>
      </div>
      <div class="form-group">
        <label class="control-label">{{ $t('schedule.teacherName') }}</label>
        <div class="searchable-select">
          <input
            type="text"
            class="form-control"
            v-model="teacherSearch"
            :placeholder="$t('typeForSearch')"
            @focus="handleTeacherFocus"
            @blur="handleTeacherBlur"
            @input="handleTeacherInput"
          />
          <div
            v-if="showTeacherDropdown && filteredTeachers.length > 0"
            class="dropdown-list"
          >
            <div
              v-for="teacher in filteredTeachers"
              :key="teacher.teacher_id"
              class="dropdown-item"
              @mousedown.prevent="selectTeacher(teacher)"
            >
              {{ getTeacherName(teacher) }}
            </div>
          </div>
        </div>
      </div>
      <div class="form-group">
        <label class="control-label">{{ $t('schedule.room') }}</label>
        <select v-model="detail.room_id" class="form-control">
          <option :value="null">{{ $t('select') }}</option>
          <option
            v-for="room in rooms"
            :key="room.room_id"
            :value="room.room_id"
            >{{ room.room_name + '|' + room.floor_no }}
            {{ room.building ? '|' + room.building.building_name : '' }}</option
          >
        </select>
      </div>
      <div class="mt-6 text-right">
        <ts-button
          class="btn-primary mr-2"
          @click.prevent="save"
          :waiting="saving"
          >{{ $t('save') }}</ts-button
        >
        <ts-button class="btn-default" @click="$emit('close')">{{
          $t('cancel')
        }}</ts-button>
      </div>
    </ts-card-wrapper>
  </ts-card>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { Errors } from 'form-backend-validation'

export default {
  name: 'schedule-class-form',
  props: ['scheduleDetail'],
  data() {
    return {
      detail: Object.assign({}, this.scheduleDetail),
      saving: false,
      errors: new Errors(),
      subjectSearch: '',
      showSubjectDropdown: false,
      isSearching: false,
      teacherSearch: '',
      showTeacherDropdown: false,
      isSearchingTeacher: false
    }
  },
  computed: {
    ...mapState('schedule', {
      subjects: 'academicSubjects',
      rooms: 'rooms'
    }),
    title() {
      if (this.scheduleDetail === null) return ''

      return (
        this.scheduleDetail.weekday.wd_name_en +
        ' (' +
        this.scheduleDetail.schedule_time.sc_from_time +
        '-' +
        this.scheduleDetail.schedule_time.sc_to_time +
        ')'
      )
    },
    teachers() {
      const subject = this.subjects.find(
        s => s.subject_id == this.detail.subject_id
      )

      if (subject === undefined) return []

      return [...subject.teachers].sort((a, b) =>
        this.getTeacherName(a).localeCompare(this.getTeacherName(b))
      )
    },
    filteredSubjects() {
      if (!this.isSearching || !this.subjectSearch) return this.subjects

      const filter = this.subjectSearch.toLowerCase()
      return this.subjects.filter(subject => {
        const searchText = (
          subject.subject_name +
          ' | ' +
          subject.subject_code
        ).toLowerCase()
        return searchText.includes(filter)
      })
    },
    filteredTeachers() {
      if (!this.isSearchingTeacher || !this.teacherSearch) return this.teachers

      const filter = this.teacherSearch.toLowerCase()
      return this.teachers.filter(teacher =>
        this.getTeacherSearchText(teacher).includes(filter)
      )
    }
  },
  mounted() {
    // Set initial search value if subject is already selected
    if (this.detail.subject_id) {
      const subject = this.subjects.find(
        s => s.subject_id == this.detail.subject_id
      )
      if (subject) {
        this.subjectSearch = subject.subject_name + ' | ' + subject.subject_code
      }
    }
    // Set initial search value if teacher is already selected
    if (this.detail.teacher_id) {
      const teacher = this.teachers.find(
        t => t.teacher_id == this.detail.teacher_id
      )
      if (teacher) {
        this.teacherSearch = this.getTeacherName(teacher)
      }
    }
  },
  methods: {
    ...mapActions('schedule', ['updateClass']),
    getTeacherName(teacher) {
      const fname = teacher.fname_native || teacher.fname_foreign || ''
      const lname = teacher.lname_native || teacher.lname_foreign || ''
      return (lname + ' ' + fname).trim()
    },
    getTeacherSearchText(teacher) {
      return [
        teacher.fname_native,
        teacher.lname_native,
        teacher.fname_foreign,
        teacher.lname_foreign
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
    },
    handleSubjectFocus() {
      this.showSubjectDropdown = true
      this.isSearching = false
      // Clear the search to show all subjects when focused
      this.subjectSearch = ''
    },
    handleSubjectInput() {
      this.showSubjectDropdown = true
      this.isSearching = true
    },
    selectSubject(subject) {
      this.detail.subject_id = subject.subject_id
      this.subjectSearch = subject.subject_name + ' | ' + subject.subject_code
      this.detail.teacher_id = null
      this.teacherSearch = ''
      this.showSubjectDropdown = false
      this.isSearching = false
    },
    handleSubjectBlur() {
      setTimeout(() => {
        this.showSubjectDropdown = false
        this.isSearching = false
        // Reset to valid selection or clear
        if (this.detail.subject_id) {
          const subject = this.subjects.find(
            s => s.subject_id == this.detail.subject_id
          )
          if (subject) {
            this.subjectSearch =
              subject.subject_name + ' | ' + subject.subject_code
          }
        } else {
          this.subjectSearch = ''
        }
      }, 200)
    },
    handleTeacherFocus() {
      this.showTeacherDropdown = true
      this.isSearchingTeacher = false
      this.teacherSearch = ''
    },
    handleTeacherInput() {
      this.showTeacherDropdown = true
      this.isSearchingTeacher = true
    },
    selectTeacher(teacher) {
      this.detail.teacher_id = teacher.teacher_id
      this.teacherSearch = this.getTeacherName(teacher)
      this.showTeacherDropdown = false
      this.isSearchingTeacher = false
    },
    handleTeacherBlur() {
      setTimeout(() => {
        this.showTeacherDropdown = false
        this.isSearchingTeacher = false
        if (this.detail.teacher_id) {
          const teacher = this.teachers.find(
            t => t.teacher_id == this.detail.teacher_id
          )
          if (teacher) {
            this.teacherSearch = this.getTeacherName(teacher)
          }
        } else {
          this.teacherSearch = ''
        }
      }, 200)
    },
    save() {
      this.errors = new Errors()
      this.saving = true

      this.updateClass({
        sch_id: this.detail.sch_id,
        data: {
          counter: this.detail.counter,
          subject_id: this.detail.subject_id,
          room_id: this.detail.room_id,
          teacher_id: this.detail.teacher_id
        }
      })
        .then(response => {
          this.saving = false

          this.$store.commit('schedule/SET_FOUND_DATA', response.schedule_class)

          this.$toasted.success(response.message)
          this.$emit('close')
        })
        .catch(error => {
          this.saving = false
          this.errors = new Errors(error.errors)

          this.$toasted.error(error.message)
        })
    }
  }
}
</script>

<style scoped>
.searchable-select {
  position: relative;
}

.dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background-color: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-top: 4px;
}

.dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
  color: #000;
  background-color: #fff;
}

.dropdown-item:hover {
  background-color: #f5f5f5;
}
</style>
